import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { Truck, MapPin, LogOut, Package, Play, CheckCircle2, XCircle, SkipForward, Navigation, KeyRound, User, Camera, Loader2, PackageCheck } from "lucide-react";

const PURPLE = "#2B1E4A";
const MAGENTA = "#B5179E";
const GOLD = "#F5B400";

function ConfirmDelivery({ order, onClose, onDone }) {
  const [otp, setOtp] = useState("");
  const [recipient, setRecipient] = useState("");
  const [notes, setNotes] = useState("");
  const [photo, setPhoto] = useState(null);
  const [photoName, setPhotoName] = useState("");
  const [gps, setGps] = useState(null);
  const [locating, setLocating] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const pickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 5 * 1024 * 1024) { toast.error("Foto muito grande (máx 5 MB)"); return; }
    const r = new FileReader();
    r.onload = () => { setPhoto(r.result); setPhotoName(f.name); };
    r.readAsDataURL(f);
  };

  const getGps = () => {
    if (!navigator.geolocation) { toast.error("Geolocalização não suportada"); return Promise.resolve(null); }
    setLocating(true);
    return new Promise((resolve) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => { setLocating(false); const g = { latitude: pos.coords.latitude, longitude: pos.coords.longitude, accuracy: pos.coords.accuracy }; setGps(g); resolve(g); },
        () => { setLocating(false); toast.error("Permita o acesso à localização para confirmar esta entrega"); resolve(null); },
        { enableHighAccuracy: true, timeout: 10000 });
    });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const g = gps || await getGps();
      const payload = { recipient_name: recipient, notes };
      if (otp.trim()) payload.otp = otp.trim();
      if (photo) payload.photo_base64 = photo;
      if (g) { payload.latitude = g.latitude; payload.longitude = g.longitude; payload.accuracy = g.accuracy; }
      await api.post(`/driver/deliveries/${order.id}/complete`, payload);
      toast.success("Entrega concluída");
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Não foi possível concluir");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-end justify-center bg-black/50 sm:items-center" data-testid="confirm-delivery-modal">
      <div className="max-h-[92vh] w-full max-w-md overflow-auto rounded-t-3xl bg-white p-5 sm:rounded-3xl">
        <div className="mb-3 flex items-center justify-between">
          <h3 className="font-display text-lg font-bold" style={{ color: PURPLE }}>Confirmar entrega</h3>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-lg border border-border" data-testid="confirm-close"><XCircle size={18} /></button>
        </div>
        <p className="mb-3 text-[13px] text-[#6B5A69]">{order.customer_name}</p>

        <label className="mb-1 flex items-center gap-1 text-[12px] font-bold text-[#6B5A69]"><KeyRound size={13} /> Código de recebimento (OTP)</label>
        <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))} inputMode="numeric" placeholder="Ex.: 4928" className="mb-3 w-full rounded-xl border border-border px-3 py-3 text-center text-lg font-bold tracking-widest" data-testid="confirm-otp" />

        <label className="mb-1 flex items-center gap-1 text-[12px] font-bold text-[#6B5A69]"><User size={13} /> Nome de quem recebeu</label>
        <input value={recipient} onChange={(e) => setRecipient(e.target.value)} placeholder="Ex.: Maria Silva" className="mb-3 w-full rounded-xl border border-border px-3 py-2.5 text-sm" data-testid="confirm-recipient" />

        <label className="mb-1 flex items-center gap-1 text-[12px] font-bold text-[#6B5A69]"><Camera size={13} /> Foto da entrega</label>
        <label className="mb-3 flex cursor-pointer items-center justify-center gap-2 rounded-xl border border-dashed border-border py-3 text-sm font-bold text-[#5B2CA0]" data-testid="confirm-photo-label">
          <Camera size={16} /> {photoName ? "Foto selecionada ✓" : "Tirar / escolher foto"}
          <input type="file" accept="image/jpeg,image/png,image/webp" capture="environment" onChange={pickPhoto} className="hidden" data-testid="confirm-photo-input" />
        </label>
        {photo && <img src={photo} alt="prévia" className="mb-3 h-32 w-full rounded-xl object-cover" />}

        <button onClick={getGps} disabled={locating} className="mb-3 flex w-full items-center justify-center gap-2 rounded-xl border border-border py-2.5 text-sm font-bold text-[#5B2CA0]" data-testid="confirm-gps">
          {locating ? <Loader2 size={15} className="animate-spin" /> : <MapPin size={15} />} {gps ? `Localização OK (±${Math.round(gps.accuracy)}m)` : "Capturar localização"}
        </button>

        <textarea value={notes} onChange={(e) => setNotes(e.target.value.slice(0, 500))} placeholder="Observação (opcional): entregue na portaria…" rows={2} className="mb-4 w-full rounded-xl border border-border px-3 py-2 text-sm" data-testid="confirm-notes" />

        <button onClick={submit} disabled={submitting} className="flex w-full items-center justify-center gap-2 rounded-xl py-3.5 text-sm font-bold text-white disabled:opacity-60" style={{ background: "#059669" }} data-testid="confirm-submit">
          {submitting ? <Loader2 size={17} className="animate-spin" /> : <PackageCheck size={17} />} Confirmar entrega
        </button>
      </div>
    </div>
  );
}

const ST = {
  READY_FOR_DELIVERY: ["Liberada", "#059669"],
  ROUTE_ASSIGNED: ["Em rota", "#C9960A"],
  OUT_FOR_DELIVERY: ["Saiu p/ entrega", "#C9960A"],
  ARRIVED: ["Chegou", "#2563EB"],
  DELIVERED: ["Entregue", "#2563EB"],
  DELIVERY_FAILED: ["Falhou", "#B45309"],
  WAITING_SIGNATURE: ["Bloqueada", "#E11D48"],
};

const addr = (a) => (a ? [a.street, a.number, a.city].filter(Boolean).join(", ") : "—");
const maps = (o) => {
  if (o && o.address_lat != null && o.address_lng != null)
    return `https://www.google.com/maps/search/?api=1&query=${o.address_lat},${o.address_lng}`;
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr(o?.delivery_address_snapshot))}`;
};

export default function DriverPortal() {
  const { user, logout } = useAuth();
  const [me, setMe] = useState(null);
  const [route, setRoute] = useState(undefined); // undefined=loading, null=none
  const [stops, setStops] = useState([]);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);

  const load = useCallback(() => {
    api.get("/driver/me").then(({ data }) => setMe(data)).catch(() => setMe(null));
    api.get("/driver/route").then(({ data }) => { setRoute(data.route); setStops(data.stops || []); })
      .catch(() => { setRoute(null); setStops([]); });
  }, []);
  useEffect(() => { load(); }, [load]);

  const act = async (fn) => {
    setBusy(true);
    try { await fn(); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
    finally { setBusy(false); }
  };
  const startRoute = () => act(async () => { await api.post("/driver/route/start"); toast.success("Rota iniciada"); });
  const startStop = (sid) => act(async () => { await api.post(`/driver/stops/${sid}/start`); toast.success("Saiu para entrega"); });
  const arriveStop = (sid) => act(async () => { await api.post(`/driver/stops/${sid}/arrive`); toast.success("Chegada registrada"); });
  const failStop = (sid) => act(async () => {
    const reason = window.prompt("Motivo da falha (opcional):") || "";
    await api.post(`/driver/stops/${sid}/fail`, { reason }); toast.success("Falha registrada");
  });
  const skipStop = (sid) => act(async () => { await api.post(`/driver/stops/${sid}/skip`, {}); toast.success("Parada pulada"); });
  const resendOtp = (oid) => act(async () => { await api.post(`/driver/deliveries/${oid}/otp/resend`); toast.success("Código reenviado à cliente"); });

  return (
    <div className="min-h-screen" style={{ background: "#F5F3F8" }} data-testid="driver-portal">
      <header className="flex items-center gap-3 px-4 py-5 text-white" style={{ background: `linear-gradient(135deg, ${PURPLE}, ${MAGENTA})`, paddingTop: "calc(env(safe-area-inset-top) + 1.1rem)" }}>
        <span className="grid h-11 w-11 place-items-center rounded-xl" style={{ background: GOLD }}><Truck size={22} className="text-[#2B1E4A]" /></span>
        <div className="flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.25em]" style={{ color: GOLD }}>CRIS ROTA · Logística Inteligente</div>
          <div className="font-display text-lg font-bold">Olá, {me?.name || user?.name || "Entregador"} 👋</div>
        </div>
        <button onClick={logout} className="grid h-10 w-10 place-items-center rounded-xl bg-white/15" data-testid="driver-logout"><LogOut size={18} /></button>
      </header>

      <main className="mx-auto max-w-md space-y-3 p-4">
        <div className="rounded-2xl bg-white p-4 shadow-sm" data-testid="driver-summary">
          <div className="text-[11px] uppercase tracking-widest text-[#9A8A98]">Sua rota de hoje</div>
          <div className="mt-1 font-display text-2xl font-bold" style={{ color: PURPLE }}>{route === undefined ? "—" : stops.length} entregas</div>
          {route && route.status === "ASSIGNED" && (
            <button onClick={startRoute} disabled={busy} className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl py-3 text-sm font-bold text-white" style={{ background: "#059669" }} data-testid="driver-start-route">
              <Play size={16} /> Iniciar rota
            </button>
          )}
          {route && route.status === "IN_PROGRESS" && <div className="mt-2 inline-block rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-bold text-emerald-700">Rota em andamento</div>}
        </div>

        {route === undefined ? <div className="p-6 text-center text-sm text-[#9A8A98]">Carregando...</div> :
          stops.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#D9CFE8] bg-white p-8 text-center" data-testid="driver-empty">
              <Package size={26} className="mx-auto mb-2" style={{ color: MAGENTA }} />
              <p className="text-sm text-[#6B5A69]">Nenhuma rota atribuída a você ainda.</p>
            </div>
          ) : stops.map((s) => {
            const o = s.order || {};
            const [label, color] = ST[o.delivery_status] || ["—", "#8A7788"];
            const started = route?.status === "IN_PROGRESS";
            const done = s.status === "FAILED" || s.status === "DELIVERED";
            return (
              <div key={s.id} className="rounded-2xl bg-white p-4 shadow-sm" data-testid={`driver-stop-${s.id}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex min-w-0 items-start gap-2">
                    <span className="grid h-7 w-7 shrink-0 place-items-center rounded-full text-[12px] font-bold text-white" style={{ background: PURPLE }}>{s.position}</span>
                    <div className="min-w-0">
                      <div className="font-display font-bold text-brand-dark">{o.customer_name || "Cliente"}</div>
                      <div className="flex items-start gap-1 text-[13px] text-[#6B5A69]"><MapPin size={14} className="mt-0.5 shrink-0" /> {addr(o.delivery_address_snapshot)}</div>
                      <div className="mt-0.5 text-[10px] uppercase tracking-wide" style={{ color: o.business_unit === "ELETROS" ? "#C9960A" : "#5B2CA0" }}>{o.business_unit === "ELETROS" ? "Eletros" : "Cris Lingerie"}</div>
                    </div>
                  </div>
                  <span className="shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold text-white" style={{ background: color }}>{s.status === "SKIPPED" ? "Pulada" : label}</span>
                </div>

                <a href={maps(o)} target="_blank" rel="noopener noreferrer" className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl py-2.5 text-sm font-bold text-white" style={{ background: PURPLE }} data-testid={`driver-nav-${s.id}`}>
                  <Navigation size={15} /> Abrir no Google Maps
                </a>

                {started && !done && (
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    {(o.delivery_status === "OUT_FOR_DELIVERY" || o.delivery_status === "ARRIVED") && (
                      <div className="col-span-2 flex items-center justify-between rounded-xl bg-[#F3EEFB] px-3 py-2 text-[12px] font-semibold text-[#5B2CA0]" data-testid={`driver-otp-status-${s.id}`}>
                        <span className="flex items-center gap-1"><KeyRound size={13} /> Código enviado à cliente</span>
                        <button onClick={() => resendOtp(o.id)} disabled={busy} className="rounded-lg border border-[#5B2CA0] px-2 py-1 text-[11px] font-bold" data-testid={`driver-resend-otp-${s.id}`}>Reenviar</button>
                      </div>
                    )}
                    {o.delivery_status === "ROUTE_ASSIGNED" && <button onClick={() => startStop(s.id)} disabled={busy} className="col-span-2 flex items-center justify-center gap-1 rounded-xl py-2.5 text-sm font-bold text-white" style={{ background: GOLD, color: PURPLE }} data-testid={`driver-start-${s.id}`}><Play size={15} /> Iniciar entrega</button>}
                    {o.delivery_status === "OUT_FOR_DELIVERY" && <button onClick={() => arriveStop(s.id)} disabled={busy} className="col-span-2 flex items-center justify-center gap-1 rounded-xl py-2.5 text-sm font-bold text-white" style={{ background: "#2563EB" }} data-testid={`driver-arrive-${s.id}`}><CheckCircle2 size={15} /> Cheguei</button>}
                    {o.delivery_status === "ARRIVED" && <button onClick={() => setConfirm({ id: o.id, customer_name: o.customer_name })} disabled={busy} className="col-span-2 flex items-center justify-center gap-1 rounded-xl py-2.5 text-sm font-bold text-white" style={{ background: "#059669" }} data-testid={`driver-confirm-${s.id}`}><PackageCheck size={15} /> Confirmar entrega</button>}
                    {(o.delivery_status === "OUT_FOR_DELIVERY" || o.delivery_status === "ARRIVED") && <button onClick={() => failStop(s.id)} disabled={busy} className="flex items-center justify-center gap-1 rounded-xl border border-red-200 py-2.5 text-sm font-bold text-red-600" data-testid={`driver-fail-${s.id}`}><XCircle size={15} /> Falha</button>}
                    <button onClick={() => skipStop(s.id)} disabled={busy} className="flex items-center justify-center gap-1 rounded-xl border border-border py-2.5 text-sm font-bold text-[#6B5A69]" data-testid={`driver-skip-${s.id}`}><SkipForward size={15} /> Pular</button>
                  </div>
                )}
                {s.status === "DELIVERED" && (
                  <div className="mt-2 flex items-center justify-center gap-1 rounded-xl bg-emerald-50 py-2.5 text-sm font-bold text-emerald-700" data-testid={`driver-delivered-${s.id}`}><PackageCheck size={15} /> Entrega concluída</div>
                )}
              </div>
            );
          })}
      </main>
      {confirm && <ConfirmDelivery order={confirm} onClose={() => setConfirm(null)} onDone={() => { setConfirm(null); load(); }} />}
    </div>
  );
}
